import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { errorToast, infoToast } from 'src/utils/toasts';
import { getAccessToken } from 'utils/authUtils';
import { store } from '../store';

type RateLimitConfig = AxiosRequestConfig & {
  __retryCount?: number;
  __rateLimited?: boolean;
};

const MAX_RATE_LIMIT_RETRIES = 3;
const BASE_BACKOFF_MS = 1000;

let rateLimitToastActive = false;

const parseRetryAfter = (headerValue?: string): number | null => {
  if (!headerValue) return null;
  const seconds = Number(headerValue);
  if (!Number.isNaN(seconds)) return seconds * 1000;
  const dateMs = Date.parse(headerValue);
  if (Number.isNaN(dateMs)) return null;
  const delay = dateMs - Date.now();
  return delay > 0 ? delay : null;
};

const getBackoffDelayMs = (error: AxiosError, retryCount: number): number | null => {
  if (retryCount >= MAX_RATE_LIMIT_RETRIES) return null;
  const retryAfterHeader = error.response?.headers?.['retry-after'];
  const retryAfterDelay = parseRetryAfter(
    Array.isArray(retryAfterHeader) ? retryAfterHeader[0] : retryAfterHeader
  );
  if (retryAfterDelay && retryAfterDelay > 0) return retryAfterDelay;
  return BASE_BACKOFF_MS * 2 ** retryCount;
};

const isAuthEndpoint = (url: string) =>
  url.includes('/users/me') || url.includes('/users/feature-flags');

const setupResponseInterceptors = (
  instance: AxiosInstance,
  options: { redirectOnAuthError: boolean }
) => {
  instance.interceptors.response.use(
    (response) => {
      const cfg = response.config as RateLimitConfig;
      if (cfg.__rateLimited) rateLimitToastActive = false;
      return response;
    },
    async (error: AxiosError) => {
      const status = error.response?.status;
      const url = error.response?.config?.url ?? '';

      if (options.redirectOnAuthError && error.response) {
        const isAuthError = status === 401 || (status === 400 && isAuthEndpoint(url));
        if (isAuthError) {
          console.error('Authentication error, redirecting to login:', error);
          window.location.assign('/login');
          return Promise.reject(error);
        }
      }

      if (status === 403) {
        console.error('Forbidden:', error);
        const title = url.split('/')[1] ?? '';
        errorToast({
          title: 'Forbidden',
          description: `You do not have permission to access this resource. ${title}`
        });
      }

      if (status === 429 && error.config) {
        const cfg = error.config as RateLimitConfig;
        const retryCount = cfg.__retryCount ?? 0;
        const delayMs = getBackoffDelayMs(error, retryCount);

        if (!rateLimitToastActive) {
          infoToast({
            title: 'Rate limited',
            description: 'Retrying shortly'
          });
          rateLimitToastActive = true;
        }

        if (delayMs === null) {
          rateLimitToastActive = false;
          errorToast({
            title: 'Rate limit reached',
            description: 'Please try again in a moment.'
          });
          return Promise.reject(error);
        }

        cfg.__retryCount = retryCount + 1;
        cfg.__rateLimited = true;

        await new Promise((resolve) => setTimeout(resolve, delayMs));
        return instance(cfg);
      }

      return Promise.reject(error);
    }
  );
};

const api = axios.create({
  baseURL: import.meta.env.VITE_API_SERVER_URL
});

const publicApi = axios.create({
  baseURL: import.meta.env.VITE_API_SERVER_URL
});

api.interceptors.request.use(async (config) => {
  const token = await getAccessToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const { impersonatedUserId } = store.getState().auth;
  if (impersonatedUserId && config.headers) {
    config.headers['X-Impersonate-User-Id'] = impersonatedUserId;
  }
  return config;
});

setupResponseInterceptors(api, { redirectOnAuthError: true });
setupResponseInterceptors(publicApi, { redirectOnAuthError: false });

export default api;
export { publicApi };
